/**
 * Public Sessions API Hook
 *
 * Fetches active published sessions without authentication.
 * Uses native fetch since no auth token is required.
 */

import { useQuery } from "@tanstack/react-query"

import type { PublicSession } from "../types"

const getApiBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL
  if (!envUrl) return "/api/v1"
  if (envUrl.endsWith("/api/v1")) return envUrl
  return `${envUrl.replace(/\/$/, "")}/api/v1`
}

const API_BASE_URL = getApiBaseUrl()

interface PublicSessionListResponse {
  items: PublicSession[]
  total: number
}

export function usePublicSessions() {
  const { data, ...rest } = useQuery({
    queryKey: ["public-sessions"],
    queryFn: async (): Promise<PublicSessionListResponse> => {
      const response = await fetch(`${API_BASE_URL}/public/sessions`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch sessions: ${response.statusText}`)
      }

      const json = await response.json()
      return (json.data ?? json) as PublicSessionListResponse
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  return {
    ...rest,
    sessions: data?.items ?? [],
  }
}
